import { useEffect, useState } from 'react';
import { cssVar } from '@going-once/tokens';
import {
  type CategoryForm,
  categoryOptions,
  formFromCategory,
  formFromTaxonomy,
  type OutputOf,
  suggestedCategories,
} from '@going-once/client';
import { api } from '../lib/api';
import { describeError } from '../lib/useAsync';
import { Box, Card, Field, Pressable, Select, Stack, Text } from '../ui/primitives';
import { ErrorNote } from '../ui/state';
import { formatCents } from './Feed';

type Taxonomy = OutputOf<'catalog.taxonomy.get'>;
type Prefill = OutputOf<'catalog.listing.prefill'>;

/**
 * The photo-first listing flow (D16) — the critical path, as a screen.
 *
 * The shape of it is the product argument: **a photo, then suggestions, then a seller
 * correcting them.** Not a form with an "auto-fill" button. So the order here is
 * deliberate — you cannot type anything until a photo exists, because a seller with a
 * bin of stock should be taking pictures, not filling in fields.
 *
 * **Suggestions are shown as suggestions.** Prefill writes nothing (D33); this screen
 * puts its output into editable fields and sends what the seller leaves there, citing
 * the artifact so `accepted_fields` records what survived. A screen that silently
 * applied the model's answer would make that number meaningless — and it is the only
 * ground-truth label the eval harness gets for free.
 *
 * **Withheld fields are named, not hidden.** When the model was unsure, the seller is
 * told which fields it declined to guess at rather than being shown a shorter form for
 * no visible reason.
 */

type Step = 'photo' | 'thinking' | 'review' | 'done';

export function NewListing({ taxonomy, onPublished }: { taxonomy: Taxonomy; onPublished: () => void }) {
  const [step, setStep] = useState<Step>('photo');
  const [error, setError] = useState<ReturnType<typeof describeError> | undefined>();
  const [listingId, setListingId] = useState<string>();
  const [preview, setPreview] = useState<string>();
  const [prefill, setPrefill] = useState<Prefill>();
  const [problems, setProblems] = useState<Record<string, string>>({});

  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [condition, setCondition] = useState('');
  /** Listing fields since D59, not attributes. */
  const [brand, setBrand] = useState('');
  const [conditionNotes, setConditionNotes] = useState('');
  const [price, setPrice] = useState('');
  /**
   * Optional, and both of them are auction terms rather than prices: a reserve is the
   * floor below which the seller would rather keep the item, and buy-it-now is the price
   * at which they would rather not run the auction at all. Blank means "no".
   */
  const [reserve, setReserve] = useState('');
  const [buyNow, setBuyNow] = useState('');
  const [attributes, setAttributes] = useState<Record<string, string>>({});

  const [categorySearch, setCategorySearch] = useState('');
  const [form, setForm] = useState<CategoryForm>();

  // The tree is Shopify's (D59): it carries Going Once's own categories' forms, and any other
  // category's comes from `catalog.category.get` (public, edge-cached) when it is chosen.
  useEffect(() => {
    if (!categoryId) return setForm(undefined);
    const known = formFromTaxonomy(taxonomy, categoryId);
    if (known) return setForm(known);
    let current = true;
    setForm(undefined);
    api()
      .query('catalog.category.get', { categoryId })
      .then(
        (category) => current && setForm(formFromCategory(category)),
        (caught: unknown) => current && setError(describeError(caught)),
      );
    return () => {
      current = false;
    };
  }, [taxonomy, categoryId]);

  /** A draft needs some category before the photo has been read; the seller's choice replaces it. */
  const firstCategory = suggestedCategories(taxonomy)[0]?.categoryId ?? '';
  const required = new Set((form?.entries ?? []).filter((entry) => entry.required).map((entry) => entry.key));
  const attributeKeys = (form?.entries ?? []).map((entry) => entry.key);
  const definitionOf = (key: string) => form?.attributes.find((attribute) => attribute.key === key);
  const labelFor = (key: string) => definitionOf(key)?.label ?? key;

  async function onPhotoChosen(file: File): Promise<void> {
    setError(undefined);
    setStep('thinking');
    setPreview(URL.createObjectURL(file));

    try {
      // A draft first, because presign needs a listing to attach the key to — and the
      // server minting the key from that listing is what stops an upload landing on
      // somebody else's object.
      const created = await api().command(
        'catalog.listing.create',
        { title: 'Untitled', categoryId: firstCategory },
        {},
      );
      const id = created.listing.listingId;
      setListingId(id);

      const target = await api().command(
        'catalog.media.presign',
        // The exact byte count: a presigned PUT signs Content-Length, so this is not a
        // ceiling and a wrong number is a signature failure.
        { listingId: id, contentType: file.type as 'image/jpeg', sizeBytes: file.size },
        {},
      );

      const upload = await fetch(target.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': target.contentType },
        body: file,
      });
      if (!upload.ok) throw new Error(`Upload failed (${upload.status}). The link may have expired — try again.`);

      await api().command('catalog.media.attach', { listingId: id, s3Key: target.key }, {});

      const suggested = await api().command('catalog.listing.prefill', { listingId: id }, {});
      setPrefill(suggested);
      setTitle(suggested.suggestion.title ?? '');
      setCategoryId(suggested.suggestion.categoryId ?? firstCategory);
      setCondition(suggested.suggestion.condition ?? '');
      setBrand(suggested.suggestion.brand ?? '');
      setConditionNotes(suggested.suggestion.conditionNotes ?? '');
      setPrice(
        suggested.suggestion.suggestedStartPriceCents
          ? String(suggested.suggestion.suggestedStartPriceCents / 100)
          : '',
      );
      setAttributes(
        Object.fromEntries(
          Object.entries(suggested.suggestion.attributes ?? {}).map(([key, value]) => [key, String(value)]),
        ),
      );
      setStep('review');
    } catch (caught) {
      setError(describeError(caught));
      setStep('photo');
    }
  }

  async function onPublish(): Promise<void> {
    if (!listingId) return;
    setError(undefined);
    setProblems({});

    try {
      const current = await api().query('catalog.listing.get', { listingId });
      await api().command(
        'catalog.listing.update',
        {
          listingId,
          version: current.listing.version,
          title,
          categoryId,
          ...(condition ? { condition } : {}),
          ...(brand.trim() ? { brand: brand.trim() } : {}),
          ...(conditionNotes.trim() ? { conditionNotes: conditionNotes.trim() } : {}),
          attributes: Object.fromEntries(Object.entries(attributes).filter(([, value]) => value !== '')),
          // **No sale-mode control on this form**: everything listed here goes to
          // auction. Fixed-price listing is a real sale mode the domain supports and a
          // screen nobody has designed yet, so the choice is made here rather than
          // offered as a half-built toggle.
          saleModes: ['auction'],
          // Dollars in the field, integer cents on the wire. The conversion happens once,
          // here, because a float traveling through the system is a rounding bug later.
          startPriceCents: Math.round(Number(price) * 100),
          ...(reserve.trim() ? { reserveCents: Math.round(Number(reserve) * 100) } : {}),
          ...(buyNow.trim() ? { binCents: Math.round(Number(buyNow) * 100) } : {}),
          // What the seller kept, recorded against the artifact.
          ...(prefill ? { fromPrefill: prefill.artifactId } : {}),
        },
        {},
      );

      const updated = await api().query('catalog.listing.get', { listingId });
      await api().command('catalog.listing.publish', { listingId, version: updated.listing.version }, {});
      setStep('done');
      onPublished();
    } catch (caught) {
      const described = describeError(caught);
      // Attribute problems come back as a list so the seller fixes the form once rather
      // than discovering one field at a time.
      const details = (caught as { details?: { problems?: { key: string; message: string }[] } })?.details;
      if (details?.problems) {
        setProblems(Object.fromEntries(details.problems.map((problem) => [problem.key, problem.message])));
        setError({ message: 'Some fields need attention before this can be published.' });
      } else {
        setError(described);
      }
    }
  }

  if (step === 'done') {
    return (
      <Card>
        <Stack gap="sm" align="start">
          <Text step="heading">Published</Text>
          <Text tone="fg.muted">It is in the feed now.</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="lg">
      <Text as="h1" step="display">
        New listing
      </Text>

      {error ? <ErrorNote error={error} /> : null}

      <Card>
        <Stack gap="md">
          <Stack direction="row" gap="md" align="start">
            <Box
              background="bg.sunken"
              radius="md"
              style={{
                width: cssVar('size.thumbnail'),
                height: cssVar('size.thumbnail'),
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {preview ? (
                <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Stack align="center" justify="center" style={{ height: '100%' }}>
                  <Text step="label" tone="fg.muted">
                    Photo
                  </Text>
                </Stack>
              )}
            </Box>

            <Stack gap="sm" grow>
              <Text step="heading">Start with a photo</Text>
              <Text tone="fg.muted" step="label">
                Everything else is a suggestion you can correct. A phone photo of the item as it is — no staging.
              </Text>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={step === 'thinking'}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void onPhotoChosen(file);
                }}
              />
              {step === 'thinking' ? <Text tone="fg.muted">Uploading, then looking at it…</Text> : null}
            </Stack>
          </Stack>
        </Stack>
      </Card>

      {step === 'review' && prefill ? (
        <Card>
          <Stack gap="md">
            <Stack direction="row" justify="between" align="center">
              <Text step="heading">Suggestions</Text>
              <Text step="label" tone="fg.muted">
                confidence {Math.round(prefill.confidence * 100)}%
              </Text>
            </Stack>

            {prefill.withheld.length > 0 ? (
              <Text step="label" tone="fg.muted">
                Not confident enough to suggest: {prefill.withheld.join(', ')}. Fill those in yourself.
              </Text>
            ) : null}

            <Field id="title" label="Title" value={title} onChange={setTitle} problem={problems.title} />

            <Select
              id="category"
              label="Category"
              value={categoryId}
              onChange={setCategoryId}
              options={categoryOptions(taxonomy, { search: categorySearch, selected: categoryId })}
              note="Changing this changes which details are asked for."
            />

            <Field
              id="category-search"
              label="Find a category"
              value={categorySearch}
              onChange={setCategorySearch}
              placeholder="necklace, hats, denim jacket…"
            />

            <Field
              id="brand"
              label={form?.brandRequired ? 'Brand (required to publish)' : 'Brand'}
              value={brand}
              onChange={setBrand}
              problem={problems.brand}
            />
            <Field
              id="condition"
              label="Condition"
              value={condition}
              onChange={setCondition}
              problem={problems.condition}
            />
            <Field
              id="condition-notes"
              label="Condition notes — wear or flaws a buyer should know about"
              value={conditionNotes}
              onChange={setConditionNotes}
              multiline
              problem={problems.conditionNotes}
            />
            <Field
              id="price"
              label="Starting price (dollars)"
              value={price}
              onChange={setPrice}
              problem={problems.startPriceCents}
            />
            {/* Both optional. Publish refuses a reserve under the starting price (it
                could never bind) and a buy-it-now at or under it (it would end the
                auction before a bid), so a blank field is safer than a guessed one. */}
            <Field
              id="reserve"
              label="Reserve (optional) — we won't sell below this"
              value={reserve}
              onChange={setReserve}
              problem={problems.reserveCents}
            />
            <Field
              id="buynow"
              label="Buy it now (optional) — withdrawn as soon as anyone bids"
              value={buyNow}
              onChange={setBuyNow}
              problem={problems.binCents}
            />

            {attributeKeys.length > 0 ? (
              <Stack gap="sm">
                <Text step="label" tone="fg.muted">
                  Details for this category
                </Text>
                {attributeKeys.map((key) => {
                  const definition = definitionOf(key);
                  const label = `${labelFor(key)}${required.has(key) ? ' (required to publish)' : ''}`;
                  const onChange = (value: string) => setAttributes((previous) => ({ ...previous, [key]: value }));
                  // Shopify's values (D59): a fixed list is a select of them, stored by id; a free-text
                  // attribute like Color offers them as suggestions while typing.
                  return definition?.dataType === 'enum' ? (
                    <Select
                      key={key}
                      id={`attr-${key}`}
                      label={label}
                      value={attributes[key] ?? ''}
                      onChange={onChange}
                      options={[
                        { value: '', label: '—' },
                        ...(definition.options ?? []).map((option) => ({ value: option.id, label: option.name })),
                      ]}
                    />
                  ) : (
                    <Field
                      key={key}
                      id={`attr-${key}`}
                      label={label}
                      value={attributes[key] ?? ''}
                      onChange={onChange}
                      problem={problems[key]}
                      suggestions={definition?.options?.map((option) => option.name)}
                    />
                  );
                })}
              </Stack>
            ) : null}

            <Stack direction="row" gap="sm" justify="between" align="center">
              <Text step="label" tone="fg.muted">
                {price ? `Bidding opens at ${formatCents(Math.round(Number(price) * 100))}` : ''}
              </Text>
              <Pressable variant="primary" onPress={() => void onPublish()}>
                Publish
              </Pressable>
            </Stack>
          </Stack>
        </Card>
      ) : null}
    </Stack>
  );
}
